import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useHistory,
  useParams,
} from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { isEmpty } from 'lodash';
import {
  changeToConsultMode,
  changeToCreateMode,
  changeToUpdateMode,
  failure,
  fetchByIdAsync,
  loading,
  resetModel,
  selectDespesaReceita,
  selectMode,
  setError,
  setModel,
  setResponse,
  success,
  addToList,
  updateOnList,
  changeModeTo,
  selectDespesasReceitas,
} from '../../../../../features/despesa-receita/despesaReceitaSlice';
import {
  isCreateMode,
  isConsultMode,
  isUpdateMode,
  isNoneMode,
} from '../../../../../utils/store/store-utils';
import { register, save } from '../../../../../features/despesa-receita/despesaReceitaAPI';
import QCXRegistrationFormPageTemplate from '../../../../../templates/registration-form-page/QCXRegistrationFormPageTemplate';
import useAutoChangeMode from '../../../../../utils/hooks/mode/useAutoChangeMode';
import QCXDespesaReceitaForm from '../../../../../components/despesa-receita/QCXDespesaReceitaForm';
import { getTipoModeloLabelByValue } from '../../../../../shared-components/select-tipo-modelo/QCXFinalSelectTipoModeloAutocomplete';
import { getStringOrUndefined, parseToBoolean } from '../../../../../utils/general/parse-utils';
import QCXConfirmDialog from '../../../../../shared-components/dialog/QCXConfirmDialog';

export default function DespesaReceitaRegistrationPage({ authInfo = {} }) {
  const { t } = useTranslation();

  const { id } = useParams();

  const history = useHistory();

  const dispatch = useDispatch();

  const mode = useSelector(selectMode);

  const despesaReceita = useSelector(selectDespesaReceita);
  const despesasReceitas = useSelector(selectDespesasReceitas);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMsg, setDialogMsg] = useState('');
  const [pendingSave, setPendingSave] = useState(null);
  const findDuplicate = (lista, campo, atual) =>
    lista.find(
      (d) =>
        d[campo] === true &&
        d.tenantUnidadeDeNegocio?.id === atual.tenantUnidadeDeNegocio?.id &&
        d.id !== atual.id
    );

  const isNone = useMemo(() => (
    isNoneMode(mode)
  ), [mode]);

  const isCreate = useMemo(() => (
    isCreateMode(mode)
  ), [mode]);

  const isConsult = useMemo(() => (
    isConsultMode(mode)
  ), [mode]);

  const isUpdate = useMemo(() => (
    isUpdateMode(mode)
  ), [mode]);

  const fetchById = useCallback((despesaReceitaId) => (
    dispatch(fetchByIdAsync(despesaReceitaId))
  ), []);

  const handleChangeToCreate = () => {
    dispatch(changeToCreateMode());
  };

  const handleChangeToConsult = () => {
    dispatch(changeToConsultMode());
  };

  const handleChangeToUpdate = () => {
    dispatch(changeToUpdateMode());
  };

  useAutoChangeMode((currentMode) => (
    dispatch(changeModeTo(currentMode))
  ), [isUpdate]);

  useEffect(() => {
    if (!isEmpty(id)) {
      dispatch(fetchByIdAsync(id));
      handleChangeToConsult();
    }
  }, [id]);

  const handleCancelUpdate = () => {
    if (id) {
      fetchById(id);
    }
    handleChangeToConsult();
  };

  const normalize = useCallback((unnormalizedData) => {

    const normalizedData = {
      ...unnormalizedData,
      description: getStringOrUndefined(unnormalizedData?.description),
      tipo: getStringOrUndefined(unnormalizedData?.tipo),
      planoDeConta: unnormalizedData?.planoDeConta?.id
        ? unnormalizedData?.planoDeConta
        : undefined,
      tipoRateio: getStringOrUndefined(unnormalizedData?.tipoRateio),
      tipoCalculo: unnormalizedData?.tipoCalculo?.id
        ? unnormalizedData?.tipoCalculo
        : undefined,
      moeda: unnormalizedData?.moeda?.id
        ? unnormalizedData?.moeda
        : undefined,
      operacional: parseToBoolean(unnormalizedData?.operacional),
      aduaneira: parseToBoolean(unnormalizedData?.aduaneira),
      grupoDespesaReceita: unnormalizedData?.grupoDespesaReceita?.id
        ? unnormalizedData?.grupoDespesaReceita
        : undefined,
      sda: parseToBoolean(unnormalizedData?.sda),
      icms: parseToBoolean(unnormalizedData?.icms),
    };

    return normalizedData;
  }, [
    getStringOrUndefined,
    parseToBoolean,
  ]);

  const unnormalize = useCallback((normalizedData) => {

    const unnormalizedData = {
      ...normalizedData,
      id: normalizedData?.id,
      description: getStringOrUndefined(normalizedData?.description),
      tipo: getStringOrUndefined(normalizedData?.tipo),
      planoDeConta: normalizedData?.planoDeConta?.id
        ? normalizedData?.planoDeConta
        : undefined,
      tipoRateio: getStringOrUndefined(normalizedData?.tipoRateio),
      tipoCalculo: normalizedData?.tipoCalculo?.id
        ? normalizedData?.tipoCalculo
        : undefined,
      moeda: normalizedData?.moeda?.id
        ? normalizedData?.moeda
        : undefined,
      operacional: parseToBoolean(normalizedData?.operacional),
      aduaneira: parseToBoolean(normalizedData?.aduaneira),
      grupoDespesaReceita: normalizedData?.grupoDespesaReceita?.id
        ? normalizedData?.grupoDespesaReceita
        : undefined,
      sda: parseToBoolean(normalizedData?.sda),
      icms: parseToBoolean(normalizedData?.icms),
    };

    return unnormalizedData;
  }, [
    getStringOrUndefined,
    parseToBoolean,
  ]);

  const handleDispatchSetModel = useCallback((data) => {
    const normalizedData = normalize(data);

    dispatch(
      setModel(normalizedData)
    );
  }, [normalize]);

  const create = async (data) => {
    const executeDebounced = debounce(async () => {
      const tipoModelo = getTipoModeloLabelByValue(data?.tipo);

      try {
        const response = await register(data);

        if (response?.status === 201) {
          dispatch(resetModel());

          const created = response?.data;

          handleDispatchSetModel(created);
          dispatch(addToList({ data: created }));

          const handleResultWithDebounce = debounce(() => {
            history.push(t('com.muralis.qcx.url.modulosFinanceirosDespesaReceita'));

            dispatch(success());
            dispatch(setResponse({
              status: response.status,
              data: created,
              message: t('com.muralis.qcx.mensagem.despesaReceitaCadastrada', { tipoModelo, descricao: data?.description }),
            }));
          }, 500);

          handleResultWithDebounce();
        }
      } catch (error) {
        const defaultErrorMessage = t('com.muralis.qcx.erro.erroCadastrarDespesaReceita', { tipoModelo, descricao: data?.description });
        const errorMessage = error?.response?.data?.message
          ? `${defaultErrorMessage}: ${error?.response?.data?.message}.`
          : `${defaultErrorMessage}.`;

        dispatch(failure());
        dispatch(setError({
          message: errorMessage,
        }));
      }
    }, 500);

    dispatch(loading());
    executeDebounced();
  };

  const update = async (data) => {
    const executeDebounced = debounce(async () => {
      const tipoModelo = getTipoModeloLabelByValue(data?.tipo);

      try {
        const response = await save(data);

        if (response?.status === 200) {
          const handleResultWithDebounce = debounce(() => {
            handleChangeToConsult();
            dispatch(success());

            const saved = response?.data;

            dispatch(setResponse({
              status: response.status,
              data: saved,
              message: t('com.muralis.qcx.mensagem.despesaReceitaSalva', { tipoModelo, descricao: saved?.description }),
            }));

            dispatch(setModel(saved));
            dispatch(updateOnList({ data: saved }));
            dispatch(fetchByIdAsync(saved?.id));
          }, 500);

          handleResultWithDebounce();
          handleChangeToConsult();
        }
      } catch (error) {
        const defaultErrorMessage = t('com.muralis.qcx.erro.erroSalvarDadosDespesaReceita', { tipoModelo, descricao: data?.description });
        const errorMessage = error?.response?.data?.message
          ? `${defaultErrorMessage}: ${error?.response?.data?.message}.`
          : `${defaultErrorMessage}.`;

        dispatch(failure());
        dispatch(setError({
          message: errorMessage,
        }));
      }
    }, 500);

    dispatch(loading());
    executeDebounced();
  };

  const handleSubmit = async (data) => {
    const normalizedData = normalize(data);
    const dupSda =
      normalizedData.tipo === 'DESPESA' &&
      normalizedData.sda &&
      findDuplicate(despesasReceitas, 'sda', normalizedData);

    const dupIcms =
      normalizedData.tipo === 'IMPOSTOS_ESTADUAIS' &&
      normalizedData.icms &&
      findDuplicate(despesasReceitas, 'icms', normalizedData);

    if (dupSda || dupIcms) {
      const partes = [];
      if (dupSda) partes.push(`a despesa ${dupSda?.description} com SDA ativo`);
      if (dupIcms) partes.push(`a despesa ${dupIcms?.description} como Favorecido ICMS`);
      setDialogMsg(`Já existe ${partes.join(' e ')}. Deseja substituí-la(s)?`);
      setPendingSave({ data: normalizedData, mode: isUpdate ? 'update' : 'create' });
      setDialogOpen(true);
      return;
    }

    if (isUpdate) {
      await update(normalizedData);
    } else {
      await create(normalizedData);
    }
  };

  const confirmDialog = async () => {
    setDialogOpen(false);
    if (!pendingSave) return;

    const { data, mode: pendingMode } = pendingSave;
    if (pendingMode === 'update') {
      await update(data);
    } else {
      await create(data);
    }
    setPendingSave(null);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setPendingSave(null);
  };

  const model = useMemo(() => (
    !isCreate
      ? unnormalize(despesaReceita)
      : {}
  ), [
    isCreate,
    despesaReceita,
    unnormalize,
  ]);

  const actionName = useMemo(() => {
    if (isCreate || isNone) {
      return t('com.muralis.qcx.acoes.nova');
    }
    if (isConsult) {
      return t('com.muralis.qcx.acoes.visualizar');
    }
    return t('com.muralis.qcx.acoes.alterar');
  }, [isNone, isCreate, isConsult]);

  const breadcrumbs = useMemo(() => ([
    {
      link: {
        to: '/',
        name: t('com.muralis.qcx.inicio'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.cadastros'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.moduloCadastrosFinanceiros'),
        name: t('com.muralis.qcx.financeiro.labelPlural'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.modulosFinanceirosDespesaReceita'),
        name: t('com.muralis.qcx.despesa.despesasReceitas'),
      },
    },
    {
      text: {
        name: actionName,
      },
    },
  ]), [actionName]);

  const pageTitle = useMemo(() => {
    const tipo = getTipoModeloLabelByValue(
      despesaReceita?.tipo
    ) || t('com.muralis.qcx.despesa.despesaReceita');

    return (
      isNone || isCreate
        ? t('com.muralis.qcx.despesa.novaDespesaReceita')
        : t('com.muralis.qcx.despesa.despesaReceitaExistente', { tipo, ID: despesaReceita?.description || despesaReceita?.id || '-' })
    );
  }, [
    isNone,
    isCreate,
    despesaReceita,
    getTipoModeloLabelByValue,
  ]);

  return (
    <>
      <QCXRegistrationFormPageTemplate
        pageTitle={pageTitle}
        breadcrumbs={breadcrumbs}
        isCreate={isCreate}
        isConsult={isConsult}
        isUpdate={isUpdate}
        handleChangeToCreate={handleChangeToCreate}
        handleChangeToConsult={handleChangeToConsult}
        handleChangeToUpdate={handleChangeToUpdate}
        handleCancelUpdate={handleCancelUpdate}
        authInfo={authInfo}
      >
        {(formProps) => (
          <QCXDespesaReceitaForm
            isCreate={isCreate}
            initialValues={model}
            handleSubmit={handleSubmit}
            handleChangeModel={handleDispatchSetModel}
            authInfo={authInfo}
            requiredRoles={['despesa-receita']}
            {...formProps}
          />
        )}
      </QCXRegistrationFormPageTemplate>
      <QCXConfirmDialog
        open={dialogOpen}
        title={t('com.muralis.qcx.acoes.confirmar')}
        content={dialogMsg}
        onConfirm={confirmDialog}
        onClose={closeDialog}
      />
    </>
  );
}
